import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import Button from '../components/ui/Button';
import AuthLayout from '../components/AuthLayout';
import { useResetPassword } from '../hooks/useAuth';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const ResetPassword = () => {
    const { t } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const resetPassword = useResetPassword();

    const [formData, setFormData] = useState({
        email: searchParams.get('email') || '',
        password: '',
        password_confirmation: '',
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const token = searchParams.get('token') || '';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const errors = {};
        if (formData.password !== formData.password_confirmation) {
            errors.password_confirmation = t(KEYS.RESET_PASSWORD_ERROR_PASSWORDS_MISMATCH);
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validate()) return;

        setLoading(true);
        try {
            await resetPassword.mutateAsync({
                token,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
            });
            navigate('/login?reset=1', { replace: true });
        } catch (err) {
            setError(err.message || t(KEYS.COMMON_ERROR_GENERIC));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout subtitle={t(KEYS.RESET_PASSWORD_TAGLINE)}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div className="space-y-1.5">
                    <label htmlFor="email" className="label-base">
                        {t(KEYS.COMMON_EMAIL)}
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-base"
                        placeholder="you@example.com"
                        disabled={loading}
                    />
                </div>

                {/* New password */}
                <div className="space-y-1.5">
                    <label htmlFor="password" className="label-base">
                        {t(KEYS.RESET_PASSWORD_NEW_PASSWORD)}
                    </label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="input-base"
                        placeholder="••••••••"
                        disabled={loading}
                    />
                </div>

                {/* Confirm password */}
                <div className="space-y-1.5">
                    <label htmlFor="password_confirmation" className="label-base">
                        {t(KEYS.RESET_PASSWORD_CONFIRM_PASSWORD)}
                    </label>
                    <input
                        type="password"
                        id="password_confirmation"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        required
                        className="input-base"
                        placeholder="••••••••"
                        disabled={loading}
                    />
                    {fieldErrors.password_confirmation && (
                        <p className="text-xs text-status-overdue mt-1">
                            {fieldErrors.password_confirmation}
                        </p>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="p-3 bg-status-overdue-bg text-status-overdue rounded-md text-sm border border-status-overdue/20 animate-appear">
                        {error}
                    </div>
                )}

                <Button type="submit" variant="primary" fullWidth disabled={loading}>
                    {loading ? t(KEYS.RESET_PASSWORD_SUBMITTING) : t(KEYS.RESET_PASSWORD_SUBMIT)}
                </Button>
            </form>

            <div className="mt-5 pt-5 border-t border-surface-border text-center">
                <p className="text-sm text-text-muted">
                    <Link to="/login" className="text-accent hover:text-accent-hover font-medium">
                        {t(KEYS.COMMON_BACK_TO_LOGIN)}
                    </Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default ResetPassword;
